/**
 * KAN-848: массовая проверка авторизации: по одному аккаунту, пауза между запросами,
 * прогресс и остановка. Свежие сессии отсеиваются планом (bulkAuthPlan) заранее.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  BULK_AUTH_PAUSE_MS,
  EMPTY_TALLY,
  addToTally,
  bulkAuthPlan,
  bulkSummary,
  type BulkTally,
} from '@/lib/telegramAuthCheck';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type { AuthCheckResult, TelegramAccount } from '@/types/telegramAccount';

export interface BulkAuthState {
  running: boolean;
  done: number;
  total: number;
  tally: BulkTally;
}

const IDLE: BulkAuthState = { running: false, done: 0, total: 0, tally: EMPTY_TALLY };

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function useBulkAuthCheck(pauseMs: number = BULK_AUTH_PAUSE_MS) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<BulkAuthState>(IDLE);
  const stopRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopRef.current = true;
    };
  }, []);

  const start = useCallback(
    async (accounts: ReadonlyArray<TelegramAccount>) => {
      const { targets, skippedFresh } = bulkAuthPlan(accounts);
      stopRef.current = false;
      let tally = EMPTY_TALLY;
      setState({ running: true, done: 0, total: targets.length, tally });
      for (let i = 0; i < targets.length; i += 1) {
        if (stopRef.current) break;
        if (i > 0) await wait(pauseMs);
        if (stopRef.current) break;
        const account = targets[i];
        let result: AuthCheckResult | null = null;
        try {
          result = await telegramAccountsApi.authCheck(account.account_id, account.operator_id);
        } catch {
          result = null;
        }
        tally = addToTally(tally, result);
        if (mountedRef.current) {
          setState({ running: true, done: i + 1, total: targets.length, tally });
        }
      }
      if (!mountedRef.current) return;
      setState((prev) => ({ ...prev, running: false }));
      void queryClient.invalidateQueries({ queryKey: ['telegram-accounts'] });
      toast.success(bulkSummary(tally, skippedFresh));
    },
    [pauseMs, queryClient]
  );

  const stop = useCallback(() => {
    stopRef.current = true;
  }, []);

  return { state, start, stop };
}
